import { VectorField } from './VectorField';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  life: number;
}

export class ParticleSystem {
  private gl: WebGL2RenderingContext;
  private program: WebGLProgram;
  private particles: Particle[] = [];
  private maxParticles: number = 20000;
  private vectorField: VectorField;
  
  // WebGL Buffers
  private vao: WebGLVertexArrayObject | null = null;
  private buffer: WebGLBuffer | null = null;
  
  // Float32Array for streaming data to GPU
  // Components: px, py, vx, vy, age, life
  private dataStride = 6;
  private renderData: Float32Array;

  constructor(gl: WebGL2RenderingContext, program: WebGLProgram, vectorField: VectorField) {
    this.gl = gl;
    this.program = program;
    this.vectorField = vectorField;
    this.renderData = new Float32Array(this.maxParticles * this.dataStride);
    
    this.initBuffers();
    this.prepopulate();
  }

  private initBuffers(): void {
    const gl = this.gl;
    this.vao = gl.createVertexArray();
    this.buffer = gl.createBuffer();

    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, this.renderData.byteLength, gl.DYNAMIC_DRAW);

    // layout(location = 0) in vec2 a_position;
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, this.dataStride * 4, 0);

    // layout(location = 1) in vec2 a_velocity;
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, this.dataStride * 4, 2 * 4);

    // layout(location = 2) in float a_age;
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 1, gl.FLOAT, false, this.dataStride * 4, 4 * 4);

    // layout(location = 3) in float a_life;
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 1, gl.FLOAT, false, this.dataStride * 4, 5 * 4);

    gl.bindVertexArray(null);
  }

  private prepopulate(): void {
    for (let i = 0; i < this.maxParticles; i++) {
      this.spawnParticle(true);
    }
  }

  private spawnParticle(initialized: boolean = false): void {
    const p: Particle = {
      x: (Math.random() * 2 - 1),
      y: (Math.random() * 2 - 1),
      vx: 0,
      vy: 0,
      age: initialized ? Math.random() * 5 : 0,
      life: 2 + Math.random() * 8 // Life duration between 2 and 10 seconds
    };
    
    if (this.particles.length < this.maxParticles) {
      this.particles.push(p);
    } else {
      // Recycle a dead particle if any (managed in update)
    }
  }

  public update(deltaTime: number, mousePos: { x: number; y: number }): void {
    const field = this.vectorField;
    
    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      
      // 1. Update Lifecycle (Aesthetic Transience)
      p.age += deltaTime;
      
      if (p.age >= p.life) {
        // Reset particle - 'Metabolic' recycling
        p.x = (Math.random() * 2 - 1);
        p.y = (Math.random() * 2 - 1);
        p.vx = 0;
        p.vy = 0;
        p.age = 0;
        p.life = 2 + Math.random() * 8;
      }

      // 2. Sample Vector Field
      const [fx, fy] = field.getForce(p.x, p.y, mousePos);
      
      // 3. Physics Integration
      // Apply force with a bit of drag to prevent chaotic runaway
      const drag = 0.96;
      const speedScale = 0.002;
      
      p.vx = (p.vx + fx * speedScale) * drag;
      p.vy = (p.vy + fy * speedScale) * drag;
      
      p.x += p.vx;
      p.y += p.vy;

      // 4. Boundary Awareness (Soft wrap-around)
      if (p.x > 1.1) p.x = -1.1;
      if (p.x < -1.1) p.x = 1.1;
      if (p.y > 1.1) p.y = -1.1;
      if (p.y < -1.1) p.y = 1.1;

      // 5. Update Render Buffer
      const offset = i * this.dataStride;
      this.renderData[offset] = p.x;
      this.renderData[offset + 1] = p.y;
      this.renderData[offset + 2] = p.vx;
      this.renderData[offset + 3] = p.vy;
      this.renderData[offset + 4] = p.age;
      this.renderData[offset + 5] = p.life;
    }
  }

  public render(): void {
    const gl = this.gl;
    
    gl.useProgram(this.program);
    
    // Upload the updated particle data to the GPU
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.renderData);

    gl.bindVertexArray(this.vao);
    
    // Enable additive blending for 'thought' luminance
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    
    gl.drawArrays(gl.POINTS, 0, this.particles.length);
    
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
  }

  public resize(width: number, height: number): void {
    // Adjust particle count or size scale if necessary for performance on mobile
    const pixelCount = width * height;
    const targetParticles = Math.min(this.maxParticles, Math.floor(pixelCount / 40));
    
    if (targetParticles < this.particles.length) {
        this.particles.length = targetParticles;
    }
  }
}